import {
  Spinner, Text, Flex, Box,
} from '@chakra-ui/react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import TreeView, {
  INode,
  ITreeViewState,
  NodeId,
  IBranchProps,
  EventCallback,
  INodeRendererProps,
} from 'react-accessible-treeview';

import { useInfiniteScroll } from '@app/hooks/use-infinite-scroll';
import { Group } from '@app/types/api/group';
import { Button } from '@chakra-snippets/button';

import { formatGroupNodes, GroupNodeData } from './helpers';

const ArrowIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (isOpen ? <ChevronDown /> : <ChevronRight />);

interface GroupNodeProps extends ITreeViewState {
  element: GroupNodeData;
  isBranch: boolean;
  isExpanded: boolean;
  getNodeProps: (props: { onClick: (data: unknown) => void }) => IBranchProps;
  level: number;
  handleExpand: EventCallback;
  setSelectedGroupId: (id: string) => void;
  isLoadingSubGroups: boolean;
  lastPostElementRef: (node: Element | null) => void;
  selectedGroupId: string | null;
  updateIdMapping: (element: GroupNodeData) => void;
}

const GroupNode: React.FC<GroupNodeProps> = ({
  element,
  isBranch,
  isExpanded,
  getNodeProps,
  level,
  handleExpand,
  setSelectedGroupId,
  isLoadingSubGroups,
  lastPostElementRef,
  selectedGroupId,
  updateIdMapping,
}) => {
  const hasSubgroups = isBranch
    && typeof element?.subgroupCount === 'number'
    && element.subgroupCount > 0;

  const branchNode = (isExpandedParam: unknown, elementParam: GroupNodeData) => (isLoadingSubGroups && selectedGroupId === elementParam?.id ? (
    <Spinner data-testid={`loading-subgroups-${element?.id}`} />
  ) : (
    <ArrowIcon isOpen={isExpandedParam as boolean} />
  ));

  const onHandleExpand = (
    data: React.MouseEvent<unknown, unknown> | React.KeyboardEvent<unknown>,
  ) => {
    updateIdMapping(element);
    setSelectedGroupId((element?.id || '').toString());
    handleExpand(data);
  };

  useEffect(() => {
    if (element?.isDefaultSelected) {
      updateIdMapping(element);
      setSelectedGroupId((element?.id || '').toString());
    }
  }, []);

  return (
    <Button
      variant="ghost"
      {...getNodeProps({
        onClick: (data) => onHandleExpand(data as React.MouseEvent | React.KeyboardEvent),
      })}
      paddingLeft={`${25 * (level - 1)}px`}
      ref={element?.isLast ? lastPostElementRef : null}
      title={element?.name}
      marginTop={2}
      w="100%"
      backgroundColor={
         selectedGroupId === element?.id ? 'gray.100' : ''
      }
      data-testid={`group-tree-node-${element?.id}`}
    >
      {hasSubgroups ? (
        branchNode(isExpanded, element)
      ) : (
        <Info color="transparent" />
      )}
      <Flex w="100%" gap={3} justifyContent="space-between" alignItems="center">
        <Text lineClamp={1} textAlign="start">
          {element?.name}
        </Text>
        <Text color="gray.500">{element?.count ?? 0}</Text>
      </Flex>
    </Button>
  );
};

interface GroupTreeProps {
  groups: GroupNodeData[];
  subgroups: GroupNodeData[];
  setSelectedGroupId: (id: string) => void;
  isLoadingGroups: boolean;
  selectedGroupId: string | null;
  shouldLoadMoreItems?: boolean;
  increaseDataSet: () => void;
  searchText: string;
  isLoadingMoreGroups: boolean;
  isLoadingSubGroups: boolean;
  updateIdMapping: (element: GroupNodeData) => void;
  preSelectedGroupId?: string;
}

const GroupTree: React.FC<GroupTreeProps> = ({
  groups,
  subgroups,
  setSelectedGroupId,
  isLoadingGroups,
  selectedGroupId,
  shouldLoadMoreItems = false,
  increaseDataSet,
  isLoadingMoreGroups,
  isLoadingSubGroups,
  updateIdMapping,
  preSelectedGroupId,
}) => {
  const [data, setData] = useState<INode[]>([]);
  const [subGroupMapping, setSubGroupMapping] = useState(new Map());

  const updateTreeData = useCallback(
    (list: GroupNodeData[], id: string, children: GroupNodeData[]) => {
      const groupData = list?.map((node) => {
        if (node?.id === id) {
          const updatedNode = {
            ...node,
            children: children?.map((el) => el.id),
          };
          return updatedNode;
        }
        return node;
      }) || [];
      return groupData?.concat(children);
    },
    [],
  );

  useEffect(() => {
    let groupNodes = formatGroupNodes(groups as unknown as Group[], preSelectedGroupId) || [];
    if (groupNodes?.length > 0) {
      setData(() => {
        if (subGroupMapping?.size > 0) {
          for (const [parentId, children] of subGroupMapping.entries()) {
            groupNodes = updateTreeData(
              groupNodes,
              parentId,
              children?.map((subgroup: GroupNodeData) => ({
                name: subgroup?.name,
                children: [],
                id: `${parentId}#${subgroup?.id}`,
                groupId: subgroup?.id,
                parent: parentId,
                isBranch: true,
                count: subgroup?.count,
                subgroupCount: subgroup?.subgroups?.length,
              })),
            );
          }
          return [...groupNodes];
        }
        return [...groupNodes];
      });
    }
  }, [groups, subGroupMapping, updateTreeData]);

  useEffect(() => {
    if (selectedGroupId) {
      setSubGroupMapping((prev) => {
        const newMap = new Map(prev);
        newMap.set(
          selectedGroupId,
          subgroups.length > 0 ? subgroups : [],
        );
        return newMap;
      });
    }
  }, [subgroups, selectedGroupId]);

  const lastPostElementRef = useInfiniteScroll(
    shouldLoadMoreItems,
    increaseDataSet,
  );

  const renderNode = useCallback(
    (props: INodeRendererProps) => (
      <GroupNode
        expandedIds={new Set<NodeId>()}
        disabledIds={new Set<NodeId>()}
        halfSelectedIds={new Set<NodeId>()}
        selectedIds={new Set<NodeId>()}
        controlledIds={new Set<NodeId>()}
        tabbableId=""
        isFocused={false}
        lastUserSelect=""
        {...props}
        setSelectedGroupId={setSelectedGroupId}
        isLoadingSubGroups={isLoadingSubGroups}
        lastPostElementRef={lastPostElementRef}
        selectedGroupId={selectedGroupId}
        updateIdMapping={updateIdMapping}
      />
    ),
    [
      setSelectedGroupId,
      isLoadingSubGroups,
      lastPostElementRef,
      selectedGroupId,
      updateIdMapping,
    ],
  );

  return (
    <Box w="100%">
      {data?.length > 0 && (
        <TreeView
          data={data}
          aria-label="Checkbox tree"
          propagateCollapse
          nodeRenderer={renderNode}
        />
      )}
      {(isLoadingGroups || isLoadingMoreGroups) && (
        <Spinner data-testid="loading-groups-spinner" />
      )}
      {!isLoadingGroups && groups?.length === 0 && (
        <Text>
          <Info />
          We&apos;re looking, but no groups found.
        </Text>
      )}
    </Box>
  );
};

export default GroupTree;
